import { AchatOrder, AchatOrderItem, StockMovement } from '../types';
import { productService } from './productService';

// Mock data
const mockAchatOrders: AchatOrder[] = [
  {
    id: 'achat-1',
    orderNumber: 'ACH-2024-001',
    supplierId: 'sup-1',
    status: 'en_attente_validation',
    items: [
      {
        productId: 'prod-2',
        quantity: 20,
        receivedQuantity: 0,
        unitPrice: 25.50,
        totalPrice: 510.00
      },
      {
        productId: 'prod-3',
        quantity: 10,
        receivedQuantity: 0,
        unitPrice: 8.50,
        totalPrice: 85.00
      }
    ],
    totalAmount: 595.00,
    createdBy: '2',
    notes: 'Commande urgente pour chantier Dupont',
    createdAt: '2024-12-15T09:00:00Z',
    updatedAt: '2024-12-15T09:00:00Z'
  },
  {
    id: 'achat-2',
    orderNumber: 'ACH-2024-002',
    supplierId: 'sup-2',
    status: 'en_attente_livraison',
    items: [
      {
        productId: 'prod-1',
        quantity: 500,
        receivedQuantity: 0,
        unitPrice: 0.15,
        totalPrice: 75.00
      }
    ],
    totalAmount: 75.00,
    createdBy: '2',
    validatedBy: '2',
    validatedAt: '2024-12-14T14:00:00Z',
    expectedDelivery: '2024-12-18T00:00:00Z',
    createdAt: '2024-12-14T10:00:00Z',
    updatedAt: '2024-12-14T14:00:00Z'
  },
  {
    id: 'achat-3',
    orderNumber: 'ACH-2024-003',
    supplierId: 'sup-1',
    status: 'livree_partiellement',
    items: [
      {
        productId: 'prod-2',
        quantity: 15,
        receivedQuantity: 10,
        unitPrice: 25.50,
        totalPrice: 382.50
      }
    ],
    totalAmount: 382.50,
    createdBy: '2',
    validatedBy: '2',
    validatedAt: '2024-12-10T10:00:00Z',
    receivedBy: '3',
    receivedAt: '2024-12-12T15:30:00Z',
    expectedDelivery: '2024-12-12T00:00:00Z',
    createdAt: '2024-12-10T08:00:00Z',
    updatedAt: '2024-12-12T15:30:00Z'
  }
];

class AchatService {
  async getAchatOrders(filters?: {
    search?: string;
    status?: string;
    supplierId?: string;
  }): Promise<AchatOrder[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...mockAchatOrders];
    
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(o => 
        o.orderNumber.toLowerCase().includes(search) ||
        o.notes?.toLowerCase().includes(search)
      );
    }
    
    if (filters?.status) {
      filtered = filtered.filter(o => o.status === filters.status);
    }
    
    if (filters?.supplierId) {
      filtered = filtered.filter(o => o.supplierId === filters.supplierId);
    }
    
    return filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  async getAchatOrder(id: string): Promise<AchatOrder | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockAchatOrders.find(o => o.id === id) || null;
  }

  async createAchatOrder(order: Omit<AchatOrder, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>): Promise<AchatOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const orderNumber = `ACH-${new Date().getFullYear()}-${String(mockAchatOrders.length + 1).padStart(3, '0')}`;
    
    const newOrder: AchatOrder = {
      ...order,
      id: `achat-${Date.now()}`,
      orderNumber,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockAchatOrders.push(newOrder);
    return newOrder;
  }

  async updateAchatOrder(id: string, updates: Partial<AchatOrder>): Promise<AchatOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockAchatOrders.findIndex(o => o.id === id);
    if (index === -1) throw new Error('Commande non trouvée');
    
    mockAchatOrders[index] = {
      ...mockAchatOrders[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    return mockAchatOrders[index];
  }

  async validateOrder(orderId: string, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const order = mockAchatOrders.find(o => o.id === orderId);
    if (!order) throw new Error('Commande non trouvée');
    
    if (order.status !== 'en_attente_validation') {
      throw new Error('Cette commande ne peut pas être validée');
    }
    
    order.status = 'en_attente_livraison';
    order.validatedBy = userId;
    order.validatedAt = new Date().toISOString();
    order.updatedAt = new Date().toISOString();
  }

  async receiveOrder(orderId: string, userId: string, partial: boolean = false): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const order = mockAchatOrders.find(o => o.id === orderId);
    if (!order) throw new Error('Commande non trouvée');
    
    if (!['en_attente_livraison', 'livree_partiellement'].includes(order.status)) {
      throw new Error('Cette commande ne peut pas être réceptionnée');
    }
    
    // Marquer tous les articles comme reçus (simulation)
    order.items.forEach(item => {
      if (partial) {
        // Réception partielle : 70% des articles
        item.receivedQuantity = Math.floor(item.quantity * 0.7);
      } else {
        // Réception complète
        item.receivedQuantity = item.quantity;
      }
    });
    
    // Générer les mouvements de stock
    for (const item of order.items) {
      const quantityReceived = partial 
        ? Math.floor(item.quantity * 0.7) - (item.receivedQuantity - Math.floor(item.quantity * 0.7))
        : item.quantity - (item.receivedQuantity - item.quantity);
      
      if (quantityReceived > 0) {
        const movement: StockMovement = {
          id: `mov-achat-${Date.now()}-${item.productId}`,
          productId: item.productId,
          type: 'entry',
          quantity: quantityReceived,
          reason: `Réception commande ${order.orderNumber}`,
          userId,
          createdAt: new Date().toISOString()
        };
        
        // En réalité, cela mettrait à jour le stock du produit
        // await productService.adjustStock(item.productId, quantityReceived, movement.reason, userId);
      }
    }
    
    // Déterminer le nouveau statut
    const allReceived = order.items.every(item => item.receivedQuantity >= item.quantity);
    const someReceived = order.items.some(item => item.receivedQuantity > 0);
    
    if (allReceived) {
      order.status = 'livree_totalement';
    } else if (someReceived) {
      order.status = 'livree_partiellement';
    }
    
    order.receivedBy = userId;
    order.receivedAt = new Date().toISOString();
    order.updatedAt = new Date().toISOString();
  }

  async receiveOrderItems(orderId: string, receivingQuantities: Record<string, number>, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const order = mockAchatOrders.find(o => o.id === orderId);
    if (!order) throw new Error('Commande non trouvée');
    
    if (!['en_attente_livraison', 'livree_partiellement'].includes(order.status)) {
      throw new Error('Cette commande ne peut pas être réceptionnée');
    }
    
    // Mettre à jour les quantités reçues et générer les mouvements de stock
    for (const item of order.items) {
      const quantityToReceive = receivingQuantities[item.productId] || 0;
      
      if (quantityToReceive > 0) {
        item.receivedQuantity += quantityToReceive;
        
        const movement: StockMovement = {
          id: `mov-achat-${Date.now()}-${item.productId}`,
          productId: item.productId,
          type: 'entry',
          quantity: quantityToReceive,
          reason: `Réception commande ${order.orderNumber}`,
          userId,
          createdAt: new Date().toISOString()
        };
        
        // En réalité, cela mettrait à jour le stock du produit
        // await productService.adjustStock(item.productId, quantityToReceive, movement.reason, userId);
      }
    }
    
    // Déterminer le nouveau statut
    const allReceived = order.items.every(item => item.receivedQuantity >= item.quantity);
    const someReceived = order.items.some(item => item.receivedQuantity > 0);
    
    if (allReceived) {
      order.status = 'livree_totalement';
    } else if (someReceived) {
      order.status = 'livree_partiellement';
    }
    
    order.receivedBy = userId;
    order.receivedAt = new Date().toISOString();
    order.updatedAt = new Date().toISOString();
  }

  // Helper methods
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      en_attente_validation: 'En attente validation',
      validee: 'Validée',
      en_attente_livraison: 'En attente livraison',
      livree_partiellement: 'Livrée partiellement',
      livree_totalement: 'Livrée totalement',
      annulee: 'Annulée'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      en_attente_validation: 'bg-orange-100 text-orange-800',
      validee: 'bg-green-100 text-green-800',
      en_attente_livraison: 'bg-blue-100 text-blue-800',
      livree_partiellement: 'bg-yellow-100 text-yellow-800',
      livree_totalement: 'bg-green-100 text-green-800',
      annulee: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  canUserManageAchats(userRole: string): boolean {
    return ['admin', 'responsable_depot', 'responsable_bureau'].includes(userRole);
  }

  canUserReceiveOrders(userRole: string): boolean {
    return ['admin', 'responsable_depot', 'responsable_bureau', 'responsable_equipe'].includes(userRole);
  }
}

export const achatService = new AchatService();